import { Link } from "react-router-dom";
import { Brand } from "../components/layout";
import { Button, Card, StatusIndicator, type StatusTone } from "../components/ui";
import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import { IncidentBannerView, deriveBanners } from "../components/IncidentBanner";
import { usePublicStatus } from "../hooks/usePublicStatus";
import { releaseVersion } from "../lib/config";
import type { ComponentState, IncidentSeverity, PublicPlatformStatus } from "../lib/api";

/**
 * Public status page (Phase 8, section 27), fed by `GET /status`.
 *
 * It renders ONLY the safe, secret-free projection the API returns: coarse component
 * states, active public incidents, the deployment-lock (freeze) state, and the
 * platform version. No tenant name, guild id, hostname, queue name or stack trace is
 * ever shown — those fields are not present in the public projection.
 */

const COMPONENT_LABEL: Record<string, string> = {
  "control-api": "Control API",
  database: "Database",
  cache: "Cache",
  "object-storage": "Object storage",
  worker: "Background worker",
  "bot-runtime": "Bot runtime",
  n8n: "Automation (n8n)",
  dashboard: "Dashboard",
};

function componentTone(state: ComponentState): StatusTone {
  if (state === "operational") return "ok";
  if (state === "degraded") return "warn";
  return "neutral";
}

const SEVERITY_LABEL: Record<IncidentSeverity, string> = {
  INFORMATION: "Information",
  DEGRADED: "Degraded",
  PARTIAL_OUTAGE: "Partial outage",
  MAJOR_OUTAGE: "Major outage",
  MAINTENANCE: "Maintenance",
};

export function StatusView({ data }: { data: PublicPlatformStatus }): JSX.Element {
  const activeIncidents = data.incidents.filter((i) => i.state !== "RESOLVED");
  const allOperational = data.components.every((c) => c.state === "operational") && activeIncidents.length === 0;

  return (
    <div className="landing">
      <header className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" aria-label="ODIN home">
          <Brand />
        </Link>
        <Link to="/login">
          <Button variant="secondary">Sign in</Button>
        </Link>
      </header>

      <section className="landing__hero">
        <h1>ODIN platform status</h1>
        <p>
          {allOperational
            ? "All systems operational."
            : "We’re tracking one or more active conditions — details below."}
        </p>
      </section>

      <IncidentBannerView items={deriveBanners({ incidents: activeIncidents, components: data.components })} />

      <section className="card-grid" style={{ marginTop: "1.5rem" }}>
        <Card title="Components" description="Coarse health of each platform component.">
          <div style={{ display: "grid", gap: "0.5rem" }}>
            {data.components.map((component) => (
              <StatusIndicator
                key={component.name}
                tone={componentTone(component.state)}
                label={`${COMPONENT_LABEL[component.name] ?? component.name}: ${component.state}`}
              />
            ))}
          </div>
        </Card>

        <Card title="Deployment" description="Whether ODIN can apply live changes right now.">
          <StatusIndicator
            tone={data.liveDeploymentLocked ? "ok" : "warn"}
            label={data.liveDeploymentLocked ? "Live deployment locked (freeze active)" : "Live deployment enabled"}
          />
        </Card>

        <Card title="Version" description="The running platform release.">
          <p className="mono">{data.version}</p>
          <p className="muted">Environment: {data.environment}</p>
          <p className="muted">Status page build: {releaseVersion}</p>
        </Card>
      </section>

      <section style={{ marginTop: "1.5rem" }}>
        <h2>Active incidents</h2>
        {activeIncidents.length === 0 ? (
          <p className="muted">No active incidents.</p>
        ) : (
          <div className="card-grid">
            {activeIncidents.map((incident) => (
              <Card key={`${incident.title}-${incident.startedAtIso}`} title={incident.title}>
                <p className="muted">
                  {SEVERITY_LABEL[incident.severity]} · {incident.state}
                </p>
                <p className="muted">Affects: {incident.components.join(", ") || "platform"}</p>
                <p className="muted">
                  Started {new Date(incident.startedAtIso).toUTCString()} · Updated{" "}
                  {new Date(incident.updatedAtIso).toUTCString()}
                </p>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function Status(): JSX.Element {
  const { state, refresh } = usePublicStatus();

  if (state.kind === "ready") return <StatusView data={state.data} />;

  return (
    <div className="landing">
      <header className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" aria-label="ODIN home">
          <Brand />
        </Link>
        <Link to="/login">
          <Button variant="secondary">Sign in</Button>
        </Link>
      </header>
      <section className="landing__hero">
        <h1>ODIN platform status</h1>
      </section>

      {state.kind === "loading" ? <LoadingState label="Loading platform status…" /> : null}
      {state.kind === "not-configured" ? (
        <NotConfiguredState title="Status source not configured">
          This status page needs <code>PUBLIC_STATUS_URL</code> or <code>PUBLIC_API_URL</code> at build time.
        </NotConfiguredState>
      ) : null}
      {state.kind === "unavailable" ? (
        <div className="state" role="alert">
          <ApiUnavailableState detail={state.detail} />
          <Button variant="secondary" onClick={refresh}>
            Retry
          </Button>
        </div>
      ) : null}
    </div>
  );
}
